import { Theme } from '@mui/material';
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { CompanyRegistrationDto } from '../api';
import { useWindowDimensions } from '../utils/customHooks';
import { FillHeight } from '../utils/fillHeight';
import { useClientCompanyRegistrationContext } from './client/companyRegistration/context/ClientCompanyRegistrationContext';
import { useContentRefContext } from './contexts/ContentRefProvider';

interface ContentProps {
    children: ReactNode;
}

const getBackgroundColor = (theme: Theme, isAdmin: boolean, isReg: boolean, isUser: boolean, fullAccess: boolean) => {
    if (isAdmin || isReg) {
        return theme.palette.common.white;
    } else if (isUser) {
        return theme.palette.grey[200];
    } else if (fullAccess) {
        return theme.palette.common.white;
    } else {
        return theme.palette.common.black;
    }
};

const useStyles = makeStyles<{ isAdmin: boolean; isReg: boolean; isUser: boolean; fullAccess: boolean }>()((
    theme,
    { isAdmin, isReg, isUser, fullAccess }
) => {
    return {
        frameContainer: {
            overflow: 'auto',
            height: '100% !important',
            backgroundColor: getBackgroundColor(theme, isAdmin, isReg, isUser, fullAccess),
            wordBreak: 'break-word',
        },
    };
});

function AppContent(props: Readonly<ContentProps>) {
    const contentRef = useRef<HTMLDivElement>(null);
    const { width, height } = useWindowDimensions();
    const { footerSize, setFooterSize, setContentSize, appbarSize } = useContentRefContext();
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [isReg, setIsReg] = useState<boolean>(false);
    const [isUser, setIsUser] = useState<boolean>(false);
    const [fullAccess, setFullAccess] = useState<boolean>(false);
    const { classes } = useStyles({ isAdmin, isReg, isUser, fullAccess });
    const location = useLocation();
    const { registration } = useClientCompanyRegistrationContext();

    useEffect(() => {
        setFullAccess(
            registration?.registrationState === CompanyRegistrationDto.registrationState.APPROVED &&
                registration?.termsOfUseApproved === true
        );
    }, [registration]);

    useLayoutEffect(() => {
        if (contentRef.current) {
            setContentSize(contentRef.current.getBoundingClientRect().height);
        }
    }, [width, height, setFooterSize, setContentSize]);

    useEffect(() => {
        if (contentRef.current) {
            setContentSize(height - footerSize - appbarSize);
        }
    }, [contentRef.current, appbarSize, footerSize]);

    useEffect(() => {
        const isAdminPage = location.pathname.includes('/admin');
        const isCompanyRegistration = location.pathname.includes('/company-registration');
        const isUserPage = location.pathname.includes('/user') || location.pathname.includes('/news');
        setIsAdmin(isAdminPage);
        setIsReg(isCompanyRegistration);
        setIsUser(isUserPage);
    }, [location.pathname]);

    return (
        <main>
            <FillHeight>
                {(restHeight) => (
                    <div style={{ height: restHeight - footerSize, overflow: 'auto' }} ref={contentRef}>
                        <div className={classes.frameContainer}>{props.children}</div>
                    </div>
                )}
            </FillHeight>
        </main>
    );
}

export default AppContent;
